import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

import {
  Box,
  FormControlLabel,
  Link,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  DoDisturbOff,
  DoDisturbOffOutlined,
  DoDisturbOn,
  DoDisturbOnOutlined,
} from '@mui/icons-material';

import MUIDataTable from 'mui-datatables';
import TooltipButton from 'generic/components/ui/TooltipButton';

import QES_CONSTANTS from 'generic/core/qes/constants';
import { format } from 'generic/utils/dateUtils';
import { baseProptype, schedulePropType } from 'generic/core/qes/proptypes';

const { SCHEDULE } = QES_CONSTANTS;

const customToolbarSelect = (rowsSelected, data, statusFilter, handleClickActivateDeactivateSelected) => {
  const { t } = i18next;
  const isActive = statusFilter !== SCHEDULE.STATUSES.INACTIVE.join(',');
  const actionText = isActive ? t('schedules.deactivate') : t('schedules.activate');
  const actionIcon = isActive ? <DoDisturbOn /> : <DoDisturbOff />;

  const handleClick = () => handleClickActivateDeactivateSelected(rowsSelected, data, !isActive);

  return (
    <Button
      color="primary"
      variant="text"
      sx={{
        marginRight: 2,
      }}
      onClick={handleClick}
      startIcon={actionIcon}
    >
      {actionText}
    </Button>
  );
};

const customBodyRenderActions = (tableMeta, schedules, handleClickActivateDeactivate) => {
  const { t } = i18next;
  const scheduleId = tableMeta.rowData[0];
  const schedule = _.find(schedules, { schedule: scheduleId });
  const etat = schedule ? schedule.etat_schedule : null;
  const isActive = SCHEDULE.STATUSES.ACTIVE.includes(etat);

  const buttonProps = {
    tag: 'fab',
    color: 'primary',
    size: 'extraSmall',
  };

  const activateDeactivateButton = (
    <TooltipButton
      {...buttonProps}
      title={isActive ? t('schedules.deactivate') : t('schedules.activate')}
      onClick={() => handleClickActivateDeactivate(scheduleId, !isActive)}
    >
      {isActive ? <DoDisturbOnOutlined /> : <DoDisturbOffOutlined />}
    </TooltipButton>
  );

  return (
    <Stack spacing={0.5} direction="row">
      {activateDeactivateButton}
    </Stack>
  );
};

const customBodyRenderName = (value, tableMeta) => (
  <Link
    component={RouterLink}
    underline="none"
    to={`schedules/${tableMeta.rowData[0]}`}
  >
    {value}
  </Link>
);

const customBodyRenderDate = (value) => (value ? format(value) : null);

const Schedules = ({
  handleClickActivateDeactivate,
  handleClickActivateDeactivateSelected,
  setSelectedRows,
  handleStatusChange,
  bases = [],
  schedules = [],
  selectedRows = [],
  loading = false,
  statusFilter = SCHEDULE.STATUSES.ACTIVE.join(','),
}) => {
  const { t } = useTranslation();
  const basesNames = bases.reduce((acc, base) => {
    acc[base.base] = base.libelle;
    return acc;
  }, {});

  const theme = useTheme();
  const smallerThanLarge = useMediaQuery(theme.breakpoints.down('lg'));
  const smallerThanSmall = useMediaQuery(theme.breakpoints.down('sm'));
  let responsiveTableType = 'standard';
  if (smallerThanLarge) {
    responsiveTableType = 'verticalAlways';
    if (smallerThanSmall) {
      responsiveTableType = 'simple';
    }
  }

  const columns = [
    {
      name: 'id',
      options: {
        display: 'excluded',
        filter: false,
      },
    },
    {
      name: 'name',
      label: t('schedules.header.name'),
      options: {
        filter: false,
        customBodyRender: customBodyRenderName,
      },
    },
    {
      name: 'base',
      label: t('schedules.header.base'),
      options: {
        filter: true,
      },
    },
    {
      name: 'date_of_creation',
      label: t('schedules.header.creation_date'),
      options: {
        filter: false,
        customBodyRender: customBodyRenderDate,
      },
    },
    {
      name: '',
      label: t('actions.actions'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => (
          customBodyRenderActions(tableMeta, schedules, handleClickActivateDeactivate)
        ),
      },
    },
  ];

  const data = schedules.map(
    (schedule) => [
      schedule.schedule,
      schedule.libelle,
      basesNames[schedule.base],
      schedule.date_creation,
    ],
  );

  const textLabels = t('datatable.textLabels', { returnObjects: true });
  const options = {
    rowsPerPage: 20,
    rowsPerPageOptions: [],
    filterType: 'checkbox',
    fixedHeader: true,
    textLabels: {
      ...textLabels,
      body: {
        ...textLabels.body,
        noMatch: loading ? t('datatable.loading') : textLabels.body.noMatch,
      },
    },
    downloadOptions: { filename: 'schedules_list.csv' },
    onChangePage: () => {
      const table = document.querySelector('#datatable .MuiTable-root');
      table.scrollIntoView(true);
    },
    sortOrder: {
      name: 'date_of_creation',
      direction: 'desc',
    },
    responsive: responsiveTableType,
    setTableProps: () => ({ size: 'small' }),
    rowsSelected: selectedRows,
    onRowSelectionChange: (rowsSelectedData, allRows, rowsSelected) => {
      setSelectedRows(rowsSelected);
    },
    customToolbarSelect: (rowsSelected) => (
      customToolbarSelect(rowsSelected, data, statusFilter, handleClickActivateDeactivateSelected)
    ),
  };
  return (
    <Box
      sx={{
        width: '100%',
        m: smallerThanLarge ? 0 : 2,
      }}
      id="datatable"
    >
      <MUIDataTable
        title={(
          <Stack direction="row" spacing={2}>
            <Typography variant="h6" sx={{ mr: 2, lineHeight: '38px' }}>{t('schedules.list')}</Typography>
            <FormControlLabel
              control={(
                <ToggleButtonGroup
                  color="primary"
                  exclusive
                  onChange={handleStatusChange}
                  value={statusFilter}
                  size="small"
                >
                  <ToggleButton value={SCHEDULE.STATUSES.ACTIVE.join(',')}>
                    {t('schedules.active_other')}
                  </ToggleButton>
                  <ToggleButton value={SCHEDULE.STATUSES.INACTIVE.join(',')}>
                    {t('schedules.inactive_other')}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            />
          </Stack>
        )}
        data={data}
        columns={columns}
        options={options}
      />
    </Box>
  );
};

Schedules.propTypes = {
  bases: PropTypes.arrayOf(baseProptype),
  schedules: PropTypes.arrayOf(schedulePropType),
  loading: PropTypes.bool,
  handleStatusChange: PropTypes.func.isRequired,
  statusFilter: PropTypes.string,
  setSelectedRows: PropTypes.func.isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.number),
  handleClickActivateDeactivate: PropTypes.func.isRequired,
  handleClickActivateDeactivateSelected: PropTypes.func.isRequired,
};

export default Schedules;
